// Membership-number gate on partner offer pages (/nnas). The offer card stays
// hidden until a plausible member number is entered.
//
// This is a nudge, not verification: nothing is checked against the partner's
// member list, and the price is enforced by the Stripe coupon, not by this
// form. So the format check is deliberately lenient — turning away a real
// member is worse than letting a guess through.
//
// The member number itself never goes into analytics; the events carry only
// the partner slug and whether the entry was accepted.

export const GATE_SUBMITTED_EVENT = 'Partner Offer Gate Submitted';
export const OFFER_UNLOCKED_EVENT = 'Partner Offer Unlocked';

// 3–20 letters/digits, dashes allowed between them.
const MEMBER_NUMBER = /^[A-Z0-9](?:[A-Z0-9-]{1,18})[A-Z0-9]$/;

/** Trims, drops spaces and uppercases, so "nn 1234" and "NN1234" match. */
export function normaliseMemberNumber(raw: string | null | undefined): string {
  return (raw || '').replace(/\s+/g, '').toUpperCase();
}

export function isValidMemberNumber(raw: string | null | undefined): boolean {
  return MEMBER_NUMBER.test(normaliseMemberNumber(raw));
}

export function gateSubmittedProperties(partner: string, valid: boolean) {
  return { partner, result: valid ? 'valid' : 'invalid' };
}

export function unlockedStorageKey(partner: string): string {
  return `partner_offer_unlocked:${partner}`;
}

type Capture = (event: string, properties: Record<string, unknown>) => void;

// Same gate as utils/analytics/pageTracking.ts: the backend analytics toggle,
// then PostHog only exists with analytics cookie consent.
function defaultCapture(event: string, properties: Record<string, unknown>) {
  if (!(window.analyticsEnabled ?? window.mixpanelEnabled)) return;
  window.posthog?.capture(event, properties);
}

// sessionStorage can throw (private mode, blocked site data); the gate must
// still work without it, it just won't remember the unlock.
function readStored(key: string): string | null {
  try { return window.sessionStorage.getItem(key); } catch { return null; }
}

function writeStored(key: string, value: string | null) {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch { /* not remembered, still unlocked */ }
}

/**
 * Wires one `[data-partner-offer-gate="<partner>"]` block. Expects inside it:
 * [data-gate-panel], [data-gate-form], [data-gate-input], [data-gate-feedback],
 * [data-gate-offer], and optionally [data-gate-member] / [data-gate-reset].
 */
export function initPartnerOfferGate(root: HTMLElement, capture: Capture = defaultCapture) {
  const partner = root.dataset.partnerOfferGate || 'unknown';
  const panel = root.querySelector<HTMLElement>('[data-gate-panel]');
  const form = root.querySelector<HTMLFormElement>('[data-gate-form]');
  const input = root.querySelector<HTMLInputElement>('[data-gate-input]');
  const feedback = root.querySelector<HTMLElement>('[data-gate-feedback]');
  const offer = root.querySelector<HTMLElement>('[data-gate-offer]');
  const memberEcho = root.querySelector<HTMLElement>('[data-gate-member]');
  const reset = root.querySelector<HTMLElement>('[data-gate-reset]');
  if (!panel || !form || !input || !offer) return;

  const storageKey = unlockedStorageKey(partner);

  const showInvalid = (invalid: boolean) => {
    input.classList.toggle('is-invalid', invalid);
    input.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    if (feedback) feedback.hidden = !invalid;
  };

  const unlock = (memberNumber: string, focusBuy = true) => {
    if (memberEcho) memberEcho.textContent = memberNumber;
    panel.classList.add('d-none');
    offer.classList.remove('d-none');
    offer.setAttribute('aria-hidden', 'false');
    if (focusBuy) document.getElementById('purchase_button_main')?.focus();
  };

  const lock = () => {
    writeStored(storageKey, null);
    offer.classList.add('d-none');
    offer.setAttribute('aria-hidden', 'true');
    panel.classList.remove('d-none');
    input.value = '';
    showInvalid(false);
    input.focus();
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const memberNumber = normaliseMemberNumber(input.value);
    const valid = isValidMemberNumber(memberNumber);
    capture(GATE_SUBMITTED_EVENT, gateSubmittedProperties(partner, valid));

    if (!valid) {
      showInvalid(true);
      input.focus();
      return;
    }

    showInvalid(false);
    writeStored(storageKey, memberNumber);
    capture(OFFER_UNLOCKED_EVENT, { partner });
    unlock(memberNumber);
  });

  input.addEventListener('input', () => {
    if (input.classList.contains('is-invalid') && isValidMemberNumber(input.value)) showInvalid(false);
  });

  reset?.addEventListener('click', lock);

  // Back from a cancelled Stripe checkout (cancel_url is this page): skip the
  // gate rather than asking again. Not re-counted as an unlock.
  const remembered = readStored(storageKey);
  if (remembered && isValidMemberNumber(remembered)) unlock(remembered, false);
}

export function initPartnerOfferGates(doc: Document = document) {
  doc.querySelectorAll<HTMLElement>('[data-partner-offer-gate]').forEach((root) => initPartnerOfferGate(root));
}
